import { ApiGetHook, useApiGet } from "@skanska/vsa-client-auth";
import { SetStateAction, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClientError } from "../models/ClientError";
import PagedResult from "../models/PagedResult";
import { QueryFilter } from "../models/QueryFilter";
import { UserLibraryFilterState } from "../models/UserLibraryFilterState";

const EMPTY_QUERY_FILTERS: QueryFilter[] = [];

export interface ApiQuery<T> {
  data: PagedResult<T> | undefined;
  isLoading: boolean;
  error: ClientError | null;
  isRefetching: boolean;
  setSortByAscending: (value: SetStateAction<boolean>) => void;
  setSortBy: (value: SetStateAction<string>) => void;
  setQuery: (value: SetStateAction<string>) => void;
  setCurrentPage: (value: SetStateAction<number>) => void;
  setFilters: (value: SetStateAction<QueryFilter[]>) => void;
  sortByAscending: boolean;
  sortBy: string;
  query: string;
  filters: QueryFilter[];
  initialStateApplied: boolean;
}

interface ApiQueryInitialStateOptions {
  initialState?: UserLibraryFilterState | null;
  useInitialState?: boolean;
  isInitialStateLoading?: boolean;
}

export function useApiQuery<T>(
  endpoint: string,
  defaultSortedColumn: string,
  defaultSortAsc: boolean,
  cacheKey: string,
  defaultFilters: QueryFilter[] = EMPTY_QUERY_FILTERS,
  pageSize: number = 50,
  initialStateOptions?: ApiQueryInitialStateOptions
): ApiQuery<T> {
  const [searchParams, setSearchParams] = useSearchParams();

  const hasQueryParam = searchParams.has("query");
  const hasFiltersParam = searchParams.has("filters");
  const hasSortByParam = searchParams.has("sortBy");
  const hasSortDirParam = searchParams.has("sortDir");
  const shouldWaitForInitialState =
    Boolean(initialStateOptions?.useInitialState) &&
    !hasQueryParam &&
    !hasFiltersParam;

  const page = searchParams.get("page");
  const pageNumber = page ? parseInt(page) : 1;

  const searchQuery = searchParams.get("query") ?? "";
  const sortByParam = searchParams.get("sortBy") ?? defaultSortedColumn;
  const sortDirParam = searchParams.get("sortDir");
  const sortAsc =
    sortDirParam !== null ? sortDirParam === "asc" : defaultSortAsc;
  const filterString = searchParams.get("filters") ?? "";

  const initialState = initialStateOptions?.initialState;

  const [query, setQuery] = useState<string>(searchQuery);
  const [sortBy, setSortBy] = useState<string>(sortByParam);
  const [sortByAscending, setSortByAscending] = useState<boolean>(sortAsc);

  const [filters, setFilters] = useState<QueryFilter[]>(
    filterString ? (JSON.parse(filterString) as QueryFilter[]) : defaultFilters
  );

  const [currentPage, setCurrentPage] = useState<number>(pageNumber);

  const [initialStateApplied, setInitialStateApplied] = useState<boolean>(
    !shouldWaitForInitialState
  );

  useEffect(() => {
    let cancelled = false;

    const applyInitialState = async () => {
      await Promise.resolve();

      if (cancelled) {
        return;
      }

      if (!shouldWaitForInitialState) {
        setInitialStateApplied(true);
        return;
      }

      if (initialStateApplied) {
        return;
      }

      if (initialStateOptions?.isInitialStateLoading) {
        return;
      }

      const nextQuery = initialState?.query ?? "";
      const nextSortBy = hasSortByParam
        ? sortByParam
        : (initialState?.sortBy ?? defaultSortedColumn);
      const nextSortByAscending = hasSortDirParam
        ? sortAsc
        : (initialState?.ascending ?? defaultSortAsc);
      const nextFilters = initialState?.filters ?? defaultFilters;

      setQuery(nextQuery);
      setSortBy(nextSortBy);
      setSortByAscending(nextSortByAscending);
      setFilters(nextFilters);
      setCurrentPage(1);

      if (initialState) {
        const nextSearchParams = new URLSearchParams(searchParams);

        if (nextQuery) {
          nextSearchParams.set("query", nextQuery);
        } else {
          nextSearchParams.delete("query");
        }

        if (nextSortBy) {
          nextSearchParams.set("sortBy", nextSortBy);
          nextSearchParams.set("sortDir", nextSortByAscending ? "asc" : "desc");
        } else {
          nextSearchParams.delete("sortBy");
          nextSearchParams.delete("sortDir");
        }

        if (nextFilters.length > 0) {
          nextSearchParams.set("filters", JSON.stringify(nextFilters));
        } else {
          nextSearchParams.delete("filters");
        }

        nextSearchParams.delete("page");

        setSearchParams(nextSearchParams, {
          preventScrollReset: true,
        });
      }

      setInitialStateApplied(true);
    };

    void applyInitialState();

    return () => {
      cancelled = true;
    };
  }, [
    defaultFilters,
    defaultSortAsc,
    defaultSortedColumn,
    hasSortByParam,
    hasSortDirParam,
    initialState,
    initialStateApplied,
    initialStateOptions?.isInitialStateLoading,
    searchParams,
    setSearchParams,
    shouldWaitForInitialState,
    sortAsc,
    sortByParam,
  ]);

  const apiQuery = useApiQueryRequest<T>(
    endpoint,
    cacheKey,
    currentPage,
    pageSize,
    query,
    sortBy,
    sortByAscending,
    filters,
    initialStateApplied
  );

  return {
    data: apiQuery.data,
    isLoading: apiQuery.isLoading,
    isRefetching: apiQuery.isRefetching,
    error: apiQuery.error,
    setSortByAscending,
    setSortBy,
    setQuery,
    setCurrentPage,
    setFilters,
    sortByAscending,
    sortBy,
    query,
    filters,
    initialStateApplied,
  };
}

function useApiQueryRequest<T>(
  baseEndpoint: string,
  cacheKey: string,
  page: number,
  pageSize: number,
  query?: string,
  sortBy?: string,
  sortAsc?: boolean,
  filters?: QueryFilter[],
  enabled: boolean = true
): ApiGetHook<PagedResult<T>> {
  // Check if baseEndpoint already has query parameters
  const hasQueryParams = baseEndpoint.includes("?");
  const separator = hasQueryParams ? "&" : "?";

  let endpoint = `${baseEndpoint}${separator}page=${page}&pageSize=${pageSize}`;
  const cacheOptions = {
    page: page,
    pageSize,
    query: undefined as string | undefined,
    sortBy: undefined as string | undefined,
    sortAsc: undefined as boolean | undefined,
    filters: undefined as string | undefined,
  };

  if (query) {
    endpoint += `&query=${encodeURIComponent(query)}`;
    cacheOptions.query = query;
  }

  if (sortBy) {
    endpoint += `&sortBy=${sortBy}`;
    cacheOptions.sortBy = sortBy;
  }

  if (sortAsc !== undefined) {
    endpoint += `&ascending=${sortAsc}`;
    cacheOptions.sortAsc = sortAsc;
  }

  if (filters && filters.length > 0) {
    const serializedFilters = JSON.stringify(filters);
    endpoint += `&filters=${encodeURIComponent(serializedFilters)}`;
    cacheOptions.filters = serializedFilters;
  }

  return useApiGet<PagedResult<T>>(endpoint, null, [cacheKey, cacheOptions], {
    enabled,
  });
}
